import * as React from "react";
import { FaThumbsDown, FaThumbsUp } from "react-icons/fa";
import styled from "styled-components";

import { ClickableToggleDisplay } from "../../../../../App/Common/Components/FormComponents";
import { PreferredLocale } from "../../../../../utils/locales";
import { HTMLEditor } from "../../../html-editor/Editor.container";
import { HTMLRenderer } from "../../../html-editor/Renderer";
import { IHTMLEditorProps } from "../../../html-editor/types";

interface IAnswerResponseBoxProps {
  correct: boolean;
}

export const AnswerResponseBox = styled.div<IAnswerResponseBoxProps>`
  align-items: center;
  background: ${props => (props.correct ? "lightgreen" : "pink")};
  border-radius: 0.5rem;
  display: flex;
  margin: 1rem 2rem;
  min-height: 5rem;
  padding-left: 1rem;
  padding-right: 1rem;
  width: auto;
`;

export interface IEditReinforcementRebukeProps {
  currentLocale: PreferredLocale;
  rebuke: Nullable<string>;
  reinforcement: Nullable<string>;
  setRebuke: (message: string, currentLocale: PreferredLocale) => void;
  setReinforcement: (message: string, currentLocale: PreferredLocale) => void;
}

export interface IEditReinforcementRebukeState {
  editRebuke: boolean;
  editReinforcement: boolean;
}

export default class EditReinforcementRebuke extends React.Component<
  IEditReinforcementRebukeProps,
  IEditReinforcementRebukeState
> {
  public reinforcementContainerRef = React.createRef<HTMLDivElement>();
  public rebukeContainerRef = React.createRef<HTMLDivElement>();
  private defaultReinforcementText: string = "Click to add reinforcement";
  private defaultRebukeText: string = "Click to add rebuke";

  constructor(props: IEditReinforcementRebukeProps) {
    super(props);
    this.state = {
      editRebuke: false,
      editReinforcement: false,
    };
  }

  public toggleEditState = (key: keyof IEditReinforcementRebukeState) => () =>
    this.setState({ [key]: !this.state[key] } as any);

  public getReinforcement = () => this.props.reinforcement || "";
  public getRebuke = () => this.props.rebuke || "";

  public setReinforcement = (message: string, currentLocale: PreferredLocale) => {
    if (message !== this.props.reinforcement) {
      this.props.setReinforcement(message, currentLocale);
    }
    this.toggleEditState("editReinforcement")();
  };

  public setRebuke = (message: string, currentLocale: PreferredLocale) => {
    if (message !== this.props.rebuke) {
      this.props.setRebuke(message, currentLocale);
    }
    this.toggleEditState("editRebuke")();
  };

  public editorShouldAutoSave = (editor: "editReinforcement" | "editRebuke") => (
    newProps: IHTMLEditorProps,
    oldProps: IHTMLEditorProps
  ): boolean => {
    return this.state[editor] && newProps.currentLocale !== oldProps.currentLocale;
  };

  public editorShouldResetState = (newProps: IHTMLEditorProps, oldProps: IHTMLEditorProps): boolean => {
    return newProps.initialContent !== oldProps.initialContent || newProps.currentLocale !== oldProps.currentLocale;
  };

  public render() {
    const { currentLocale, rebuke, reinforcement } = this.props;
    return (
      <>
        <AnswerResponseBox correct={true}>
          <FaThumbsUp />
          <ClickableToggleDisplay
            shouldDisplay={!this.state.editReinforcement}
            onClick={this.toggleEditState("editReinforcement")}
          >
            <HTMLRenderer source={reinforcement || this.defaultReinforcementText} />
          </ClickableToggleDisplay>
          {this.state.editReinforcement && (
            <div ref={this.reinforcementContainerRef}>
              <HTMLEditor
                initialContent={reinforcement || ""}
                currentLocale={currentLocale}
                getContent={this.getReinforcement}
                setContent={this.setReinforcement}
                shouldAutoSave={this.editorShouldAutoSave("editReinforcement")}
                shouldResetState={this.editorShouldResetState}
                shouldAutoFocus={true}
                containerRef={this.reinforcementContainerRef}
              />
            </div>
          )}
        </AnswerResponseBox>
        <AnswerResponseBox correct={false}>
          <FaThumbsDown />
          <ClickableToggleDisplay shouldDisplay={!this.state.editRebuke} onClick={this.toggleEditState("editRebuke")}>
            <HTMLRenderer source={rebuke || this.defaultRebukeText} />
          </ClickableToggleDisplay>
          {this.state.editRebuke && (
            <div ref={this.rebukeContainerRef}>
              <HTMLEditor
                initialContent={rebuke || ""}
                currentLocale={currentLocale}
                getContent={this.getRebuke}
                setContent={this.setRebuke}
                shouldAutoSave={this.editorShouldAutoSave("editRebuke")}
                shouldResetState={this.editorShouldResetState}
                shouldAutoFocus={true}
                containerRef={this.rebukeContainerRef}
              />
            </div>
          )}
        </AnswerResponseBox>
      </>
    );
  }
}
